/**
 * useStreamingRun — Hook to wire SSE streaming into the workflow run flow.
 *
 * Provides a `handleStreamingDraftTrigger` callback that:
 * 1. Acquires a token (guards against null)
 * 2. Builds MonitoringFetchOptions
 * 3. Calls streamingRunStore.startStream() — awaits POST success/failure
 * 4. Returns { success: true } if POST succeeded, or { success: false, error } on failure
 *
 * Only used for draft workflows when user clicks Run.
 * For all other cases, the existing polling flow is unchanged.
 */

import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import { useAppStore } from '@/stores/appStore';
import { useStreamingRunStore } from '@/stores/streamingRunStore';
import type { MonitoringFetchOptions } from '@/services/monitoringApi';
import type { StreamingStatus } from '@/stores/streamingRunStore';
import type { RunStreamError } from '@/types/streamingMonitoring';
import type { StreamingTriggerResult } from '@/components/WorkflowDesigner/TriggerWorkflowDialog';
import { useShallow } from 'zustand/react/shallow';
import { telemetryService } from '@/services/telemetryService';
import { appFetch, getAppUrl } from '@/services/appClient';
import { apiVersions } from '@/services/endpoints';

/**
 * Build MonitoringFetchOptions from the current app context.
 * Returns null if context isn't ready.
 */
function useMonitoringFetchOptions(): {
  buildOptions: (token: string) => MonitoringFetchOptions | null;
  workflowName: string | undefined;
  isReady: boolean;
} {
  const { workflowName } = useParams<{ workflowName: string }>();
  const { selectedProject, selectedApp } = useAppStore();
  const isReady = !!selectedProject && !!selectedApp && !!workflowName;

  const buildOptions = useCallback(
    (token: string): MonitoringFetchOptions | null => {
      if (!selectedProject || !selectedApp || !workflowName) return null;
      return {
        projectName: selectedProject,
        appName: selectedApp.name,
        workflowName,
        accessToken: token,
      };
    }, [selectedProject, selectedApp, workflowName],
  );

  return { buildOptions, workflowName, isReady };
}

export interface UseStreamingRunResult {
  /**
   * Callback for TriggerWorkflowDialog's `onStreamingDraftTrigger` prop.
   * Tries to start streaming; returns { success: true } if POST succeeded,
   * or { success: false, error } with error message on failure.
   * The streaming POST both triggers the run and streams events (replaces regular trigger).
   * Dialog should only close / enter execution mode when success is true.
   */
  handleStreamingDraftTrigger: (triggerName: string, body?: unknown) => Promise<StreamingTriggerResult>;

  /** Current streaming status */
  status: StreamingStatus;

  /** Whether streaming is actively receiving events */
  isStreaming: boolean;

  /** Whether the stream has completed (either success or error) */
  isComplete: boolean;

  /** Last error from the stream */
  lastError: RunStreamError | null;

  /** Stop the current stream */
  stopStream: () => void;

  /** Whether streaming is supported (context is ready) */
  isReady: boolean;
}

export function useStreamingRun(): UseStreamingRunResult {
  const { getOttoToken } = useAuth();
  const { buildOptions, isReady } = useMonitoringFetchOptions();

  const { status, lastError } = useStreamingRunStore(
    useShallow((s) => ({ status: s.status, lastError: s.lastError } as { status: StreamingStatus; lastError: RunStreamError | null })),
  );
  const startStream = useStreamingRunStore((s) => s.startStream);
  const stopStream = useStreamingRunStore((s) => s.stopStream);

  /**
   * For TriggerWorkflowDialog: attempts streaming trigger, returns result object.
   * The streaming POST both triggers the run AND streams monitoring events.
   * If success is true, the dialog should NOT also call the regular trigger.
   * If success is false, the error message explains why streaming failed.
   */
  const handleStreamingDraftTrigger = useCallback(
    async (triggerName: string, body?: unknown): Promise<StreamingTriggerResult> => {
      // H3: Guard against null token — don't send an empty bearer
      const token = await getOttoToken();
      if (!token) {
        telemetryService.trackEvent('sse.runstream.null_token', {
          context: 'handleStreamingDraftTrigger',
        });
        return { success: false, error: 'Failed to acquire authentication token' };
      }

      const opts = buildOptions(token);
      if (!opts) {
        return { success: false, error: 'Application context not ready' };
      }

      try {
        const callbackInfo = await appFetch<{ value: string; }>(`${getAppUrl(opts)}/workflows/${opts.workflowName}/triggers/${triggerName}/listDraftStreamCallbackUrl?api-version=${apiVersions.otto}`, token, { method: 'POST'});

        if (!callbackInfo?.value) {
          telemetryService.trackEvent('sse.runstream.no_callback_url', {
            context: 'handleStreamingDraftTrigger',
          });
          console.error('No callback URL returned for streaming run trigger');
          return { success: false, error: 'Failed to get streaming callback URL', useFallback: true };
        }

        const streamStarted = await startStream(opts, triggerName, callbackInfo.value, body);
        if (streamStarted) {
          // The run ID from the HTTP response headers is stored immediately by
          // onPostSuccess in streamingRunStore (before any SSE events arrive).
          // Read it back so we can navigate directly to the correct run.
          const { runId: storeRunId } = useStreamingRunStore.getState();
          const runId =
            storeRunId && storeRunId !== 'pending' && !storeRunId.startsWith('unknown-')
              ? storeRunId
              : undefined;
          return runId ? { success: true, runId } : { success: true };
        }

        // Stream failed to start — check whether fallback is safe.
        // If we received an HTTP error response (httpStatus exists), the backend
        // explicitly rejected the request and no run was created → safe to fall back.
        // If there's no httpStatus (CORS block, "Failed to fetch", network error),
        // the request may have reached the backend → NOT safe, show error instead.
        const storeError = useStreamingRunStore.getState().lastError;
        const safeFallback = !!storeError?.httpStatus;
        telemetryService.trackEvent('sse.runstream.start_error', {
          context: 'handleStreamingDraftTrigger',
          error: storeError?.message ?? 'unknown',
          httpStatus: storeError?.httpStatus ? String(storeError.httpStatus) : 'none',
          useFallback: String(safeFallback),
          workflowName: opts.workflowName,
        });
        return {
          success: false,
          error: storeError?.message ?? 'Failed to start streaming run',
          ...(safeFallback && { useFallback: true }),
        };
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error starting streaming run';
        telemetryService.trackEvent('sse.runstream.start_failed', {
          context: 'handleStreamingDraftTrigger',
          error: errorMessage,
        });
        console.error('Failed to start streaming run trigger', error);
        return { success: false, error: errorMessage, useFallback: true };
      }
    },
    [getOttoToken, buildOptions, startStream],
  );

  return {
    handleStreamingDraftTrigger,
    status,
    isStreaming: status === 'connecting' || status === 'streaming',
    isComplete: status === 'completed' || status === 'error' || status === 'aborted',
    lastError,
    stopStream,
    isReady,
  };
}
